import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackGameReady } from '../../helpers/analytics';
import { detectClientLaunchFormat } from '../../helpers/detectClientLaunchFormat';
import { launchGame } from '../../helpers/gameLauncher';
import { openLegacyGameInSameTab } from '../../helpers/legacyIframeLaunch';
import styles from './GamePlayPage.module.css';

export function GamePlayPage() {
    const [searchParams] = useSearchParams();
    const gameUrl = searchParams.get('gameUrl');
    const gameName = searchParams.get('gameName') ?? 'game';
    const mountRef = useRef<HTMLDivElement>(null);
    const cleanupRef = useRef<(() => void | Promise<void>) | null>(null);
    const pendingCleanupRef = useRef<Promise<void>>(Promise.resolve());
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !gameUrl) {
            return;
        }

        let cancelled = false;

        void (async () => {
            await pendingCleanupRef.current;
            if (cancelled) {
                return;
            }

            try {
                const format = await detectClientLaunchFormat(gameUrl);
                if (cancelled) {
                    return;
                }

                // LEGACY guard: /play is for canonical embeds only. Redirect unrebuilt dists.
                if (format === 'legacy') {
                    openLegacyGameInSameTab(gameUrl);
                    return;
                }

                const cleanup = await launchGame(mount, gameUrl, {
                    onGameReady: () => trackGameReady(gameName),
                    isStale: () => cancelled,
                });
                if (cancelled) {
                    await cleanup();
                    return;
                }
                cleanupRef.current = cleanup;
            } catch (error) {
                console.error('[GamePlayPage] Failed to launch game:', error);
                if (!cancelled) {
                    setStatusMessage('Failed to launch game.');
                }
            }
        })();

        return () => {
            cancelled = true;
            const cleanup = cleanupRef.current;
            cleanupRef.current = null;
            pendingCleanupRef.current = (async () => {
                if (cleanup) {
                    await cleanup();
                }
            })();
        };
    }, [gameUrl, gameName]);

    if (!gameUrl) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>Missing game URL.</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {statusMessage ? <p className={styles.error}>{statusMessage}</p> : null}
            <div ref={mountRef} className={styles.mount} />
        </div>
    );
}
